import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

// Marks the two history entries the guard owns, via react-router location
// state (which lives in window.history.state, so it survives a reload):
//  - 'sentinel': a copy of the trail's base route sitting directly UNDER the
//    real one. The phone's back button lands here instead of leaving.
//  - 'top': the entry the player actually plays on.
const GUARD_KEY = 'mtLeaveGuard';
type GuardMark = 'sentinel' | 'top';

/**
 * Location state for a base-route entry pushed from INSIDE the trail (the
 * phone shell's "Show on map"), so the guard treats it as the entry being
 * played on rather than a new arrival to put a spare entry under.
 */
export function guardTopState(): Record<string, GuardMark> {
  return { [GUARD_KEY]: 'top' };
}

function readMark(state: unknown): GuardMark | undefined {
  if (state && typeof state === 'object' && GUARD_KEY in state) {
    // history.state is writable by anything on the page: only trust the two
    // values the guard itself writes, so junk re-arms instead of disarming.
    const mark = (state as Record<string, unknown>)[GUARD_KEY];
    if (mark === 'sentinel' || mark === 'top') return mark;
  }
  return undefined;
}

// The trail's base route is `/trail/:code`; anything deeper is a modal route
// (a page, directions, the scanner) whose own back step just closes it. Same
// segment count Trail.tsx already uses to tell the two apart.
function isBaseTrailRoute(pathname: string): boolean {
  // `/trail/:code/` is still the base route.
  return pathname.replace(/\/+$/, '').split('/').length === 3;
}

export interface UseLeaveTrailGuardOptions {
  /** Guard only once there is a trail on screen worth protecting. */
  active: boolean;
  /** Offered every back press before the confirm shows. Return true when the
   * press was used for something else (closing the menu, say). */
  onBack?: () => boolean;
}

export interface UseLeaveTrailGuardResult {
  confirmOpen: boolean;
  stay: () => void;
  leave: () => void;
}

/**
 * "Are you sure you want to leave?" for the phone's back button. A web page
 * cannot veto a back navigation, so this is the usual history trick: keep a
 * spare entry under the trail, and when the player backs onto it, step
 * straight forward again and ask. Leaving goes to the start screen, from
 * where back returns to the trail with progress intact.
 *
 * Best-effort by nature: browsers skip history entries a page added before
 * the player had touched it, so arming waits for a first tap when needed.
 */
export function useLeaveTrailGuard({
  active,
  onBack,
}: UseLeaveTrailGuardOptions): UseLeaveTrailGuardResult {
  const history = useHistory();
  const location = useLocation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const leavingRef = useRef(false);
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;

  useEffect(() => {
    if (!active || leavingRef.current) return;

    // Read the LIVE location, not this render's: StrictMode re-runs the
    // effect with the same closure, and arming twice would stack sentinels.
    const pushTop = () => {
      const current = history.location;
      history.push({
        pathname: current.pathname,
        search: current.search,
        state: { ...(current.state as object), [GUARD_KEY]: 'top' },
      });
    };

    const arm = () => {
      const current = history.location;
      if (leavingRef.current || !isBaseTrailRoute(current.pathname)) return;
      if (readMark(current.state)) return;
      history.replace({
        pathname: current.pathname,
        search: current.search,
        state: { ...(current.state as object), [GUARD_KEY]: 'sentinel' },
      });
      pushTop();
    };

    const current = history.location;
    if (!isBaseTrailRoute(current.pathname)) return;
    const mark = readMark(current.state);
    if (mark === 'top') return;

    if (mark === 'sentinel') {
      // The player pressed back off the trail. Re-arm first so a second
      // press is caught too, then ask (unless the press had another use).
      pushTop();
      if (!onBackRef.current?.()) setConfirmOpen(true);
      return;
    }

    // `userActivation` missing (older Safari, jsdom) — just arm.
    if (navigator.userActivation?.hasBeenActive ?? true) {
      arm();
      return;
    }
    const onFirstGesture = () => {
      removeListeners();
      arm();
    };
    const removeListeners = () => {
      window.removeEventListener('pointerdown', onFirstGesture);
      window.removeEventListener('keydown', onFirstGesture);
    };
    window.addEventListener('pointerdown', onFirstGesture);
    window.addEventListener('keydown', onFirstGesture);
    return removeListeners;
  }, [active, history, location]);

  const stay = useCallback(() => setConfirmOpen(false), []);

  const leave = useCallback(() => {
    leavingRef.current = true;
    setConfirmOpen(false);
    history.push('/');
  }, [history]);

  return { confirmOpen, stay, leave };
}

export default useLeaveTrailGuard;
